import { useState } from 'react';
import { VStack } from '@astryxdesign/core/VStack';
import { HStack } from '@astryxdesign/core/HStack';
import { Text } from '@astryxdesign/core/Text';
import { Button } from '@astryxdesign/core/Button';
import { Banner } from '@astryxdesign/core/Banner';
import { BellRinging, CalendarPlus } from '@phosphor-icons/react';
import { useTenant } from '../../app/tenant-context';
import { publicApi, type ClientBooking } from '../../lib/public-api';
import { pushCapability, subscribePush } from '../../lib/push';
import { tenantBase } from '../../lib/pwa';
import { buildIcs } from '../../../supabase/functions/_shared/domain/ics.ts';

type PushState = 'idle' | 'working' | 'on' | 'on_preview' | 'error' | 'denied';

/** Reminder a day before: Web Push where it really works, ICS everywhere. States are shown honestly. */
export function ReminderOffer({ booking, token }: { booking: ClientBooking; token: string }) {
  const { slug, sf } = useTenant();
  const cap = pushCapability(sf.meta.vapid_public_key);
  const [push, setPush] = useState<PushState>('idle');
  const [icsSaved, setIcsSaved] = useState(false);
  const hours = sf.profile.reminder_hours_before;

  const enablePush = async () => {
    setPush('working');
    try {
      const sub = await subscribePush(tenantBase(slug), sf.meta.vapid_public_key!);
      const r = await publicApi.pushSubscribe(slug, token, sub);
      setPush(r.delivery === 'real' ? 'on' : 'on_preview');
    } catch (e) {
      setPush((e as Error).message === 'denied' ? 'denied' : 'error');
    }
  };

  const downloadIcs = () => {
    const ics = buildIcs({
      uid: `${booking.id}@${slug}`, title: `${booking.service_name} — ${sf.profile.short_name}`,
      description: `Код записи ${booking.public_code}. Телефон студии ${sf.profile.phone}.`, location: sf.profile.address,
      start: booking.starts_at, end: booking.ends_at, reminderHoursBefore: hours,
      url: `${location.origin}${tenantBase(slug)}my`,
    });
    const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `zapis-${booking.public_code}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    setIcsSaved(true);
  };

  return (
    <VStack gap={3}>
      <Text weight="semibold">Напомнить за {hours} ч?</Text>
      {cap.kind === 'available' && push !== 'on' && push !== 'on_preview' ? (
        <Button label="Включить уведомление" icon={<BellRinging size={18} />} variant="primary" isLoading={push === 'working'} clickAction={enablePush} width="100%" />
      ) : null}
      {push === 'on' ? <Banner status="success" title="Уведомление включено" description={`Придёт за ${hours} ч до записи на это устройство.`} container="card" /> : null}
      {push === 'on_preview' ? <Banner status="info" title="Подписка сохранена" description="Это демо-студия: настоящие уведомления не отправляются." container="card" /> : null}
      {push === 'denied' || cap.kind === 'denied' ? <Banner status="warning" title="Уведомления запрещены в настройках браузера" description="Добавьте запись в календарь — он напомнит сам." container="card" /> : null}
      {push === 'error' ? <Banner status="error" title="Не удалось включить уведомление" description="Добавьте запись в календарь." container="card" /> : null}
      {cap.kind === 'needs_install' ? <Banner status="info" title="Напоминания на iPhone" description={`${cap.reason} Нажмите «Поделиться» → «На экран Домой», откройте приложение и включите напоминание в «Моя запись». Или добавьте запись в календарь.`} container="card" /> : null}
      {cap.kind === 'unsupported' || cap.kind === 'not_configured' ? <Text type="supporting">В этом браузере уведомления недоступны — добавьте запись в календарь.</Text> : null}
      <HStack gap={2}>
        <Button label={icsSaved ? 'Файл календаря скачан' : 'Добавить в календарь'} icon={<CalendarPlus size={18} />} variant="secondary" onClick={downloadIcs} width="100%" />
      </HStack>
      {icsSaved ? <Text type="supporting">Откройте скачанный файл — календарь напомнит за {hours} ч, даже без интернета.</Text> : null}
    </VStack>
  );
}
